import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Row,
  Col,
  DatePicker,
  Select,
  Input,
  Radio,
  Button,
  Card,
  Divider,
  message,
  Spin,
} from "antd";
import { useSelector } from "react-redux";
import moment from "moment";
import styled from "styled-components";
import { inspectionService, userService } from "@iso/services";
import FileUploader from "@iso/components/FileUploader/FileUploader";

const { Option } = Select;
const { TextArea } = Input;

// Lay the uploaded photo thumbnails out in two columns under the comment field
// (item 1 left, item 2 right, item 3 left under 1, …) instead of a single
// column under the Upload button. Scoped to this wrapper so other FileUploaders
// across the app keep their default single-column list.
const PhotoGrid = styled.div`
  .ant-upload-list-picture {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 8px;
  }
  /* AntD's clearfix ::before/::after otherwise become stray grid cells (leaving
     an empty top-left cell) — remove them so thumbnails fill from the top-left. */
  .ant-upload-list-picture::before,
  .ant-upload-list-picture::after {
    display: none;
  }
  /* grid gap handles spacing — cancel antd's default per-item top margin */
  .ant-upload-list-picture .ant-upload-list-item {
    margin-top: 0;
  }
`;

// Pre-start / daily inspection. Loads the default checklist, captures
// pass/fail/na per item with a photo + comment on issues, and emails the
// report to selected recipients on save.
const PreStartInspectionModal = (props) => {
  const { visible, asset, onCancel, onSaved } = props;
  const currentUser = useSelector((state) => state.user.user);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [checklist, setChecklist] = useState([]);
  const [results, setResults] = useState({}); // key -> { result, comment, imageIds: [] }
  const [users, setUsers] = useState([]);
  const [inspectionDate, setInspectionDate] = useState(moment());
  const [operatorId, setOperatorId] = useState(null);
  const [notes, setNotes] = useState("");
  const [recipients, setRecipients] = useState([]);

  const itemKey = (s, i) => `${s}_${i}`;

  const resetState = useCallback(() => {
    setResults({});
    setNotes("");
    setRecipients([]);
    setInspectionDate(moment());
    setOperatorId(currentUser ? currentUser.id : null);
  }, [currentUser]);

  useEffect(() => {
    if (!visible) return;
    setLoading(true);
    resetState();
    Promise.all([
      // Pass the asset id so its assigned custom template (if any) is loaded;
      // the backend falls back to the default checklist when none is assigned.
      inspectionService.getChecklist(asset?.id),
      asset?.property_id ? userService.getUserForProperty(asset.property_id) : Promise.resolve({}),
    ])
      .then(([checklistRes, usersRes]) => {
        const sections = (checklistRes && checklistRes.checklist) || [];
        setChecklist(sections);
        // Seed every item as 'pass' by default.
        const seeded = {};
        sections.forEach((section, si) => {
          (section.items || []).forEach((label, ii) => {
            seeded[itemKey(si, ii)] = { result: "pass", comment: "", imageIds: [] };
          });
        });
        setResults(seeded);
        setUsers((usersRes && usersRes.users) || []);
      })
      .catch(() => message.error("Failed to load inspection checklist"))
      .finally(() => setLoading(false));
    // Key only on the asset id + visibility so a parent re-render (e.g. asset
    // list refresh) doesn't re-run this and wipe in-progress entries.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, asset && asset.id]);

  const updateResult = (key, patch) => {
    setResults((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  // Use functional setResults so concurrent photo uploads on the same item
  // (FileUploader is multiple → one callback per file) don't overwrite each
  // other via a stale `results` snapshot.
  const handlePhotoSuccess = (key, image) => {
    if (!image || !image.id) return;
    setResults((prev) => {
      const cur = prev[key] || { result: "fail", comment: "", imageIds: [] };
      return { ...prev, [key]: { ...cur, imageIds: [...(cur.imageIds || []), image.id] } };
    });
  };

  const handlePhotoRemove = (key, file) => {
    const removedId = file?.response?.image?.id;
    if (!removedId) return;
    setResults((prev) => {
      const cur = prev[key] || { result: "fail", comment: "", imageIds: [] };
      return { ...prev, [key]: { ...cur, imageIds: (cur.imageIds || []).filter((id) => id !== removedId) } };
    });
  };

  const handleSave = () => {
    if (!asset) return;
    setSaving(true);

    const items = [];
    checklist.forEach((section, si) => {
      (section.items || []).forEach((label, ii) => {
        const r = results[itemKey(si, ii)] || { result: "pass" };
        items.push({
          section: section.section,
          label,
          result: r.result || "pass",
          comment: r.comment || null,
          image_ids: r.imageIds || [],
        });
      });
    });

    const payload = {
      inspection: {
        facility_id: asset.id,
        property_id: asset.property_id,
        operator_id: operatorId || null,
        inspection_date: inspectionDate ? inspectionDate.format("YYYY-MM-DD HH:mm:ss") : "",
        notes: notes || null,
        items,
        recipient_emails: recipients,
      },
    };

    inspectionService
      .storeOrUpdate(payload)
      .then((res) => {
        if (res && res.code === "0000") {
          // Reflect the real email result: warn (not blanket success) if some/all
          // recipients failed, using the server message.
          const emailFailed = res.email && res.email.failed && res.email.failed.length;
          (emailFailed ? message.warning : message.success)(
            res.message || (recipients.length ? "Inspection saved and emailed" : "Inspection saved")
          );
          if (onSaved) onSaved(res.inspection);
        } else {
          message.error((res && res.message) || "Failed to save inspection");
        }
      })
      .catch((err) => {
        console.error(err);
        message.error("Failed to save inspection. Please try again.");
      })
      .finally(() => setSaving(false));
  };

  const handleCancel = () => {
    resetState();
    if (onCancel) onCancel();
  };

  return (
    <Modal
      open={visible}
      title={`Pre-start Inspection${asset?.title ? ` — ${asset.title}` : ""}`}
      onCancel={handleCancel}
      maskClosable={false}
      width={780}
      footer={[
        <Button key="back" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={saving} onClick={handleSave}>
          Save{recipients.length ? " & Email" : ""}
        </Button>,
      ]}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <label>Inspection Date &amp; Time</label>
              <DatePicker
                value={inspectionDate}
                onChange={setInspectionDate}
                style={{ width: "100%" }}
                showTime={{ format: "HH:mm" }}
                format="DD/MM/YYYY HH:mm"
              />
            </Col>
            <Col xs={24} md={12}>
              <label>Operator</label>
              <Select
                value={operatorId}
                onChange={setOperatorId}
                style={{ width: "100%" }}
                placeholder="Select operator"
                showSearch
                allowClear
                filterOption={(input, option) =>
                  String(option.children).toLowerCase().includes(input.toLowerCase())
                }
              >
                {users.map((u) => (
                  <Option key={u.id} value={u.id}>
                    {u.name} {u.surname}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>

          {checklist.map((section, si) => (
            <div key={si}>
              <Divider orientation="left">{section.section}</Divider>
              {(section.items || []).map((label, ii) => {
                const key = itemKey(si, ii);
                const r = results[key] || { result: "pass" };
                return (
                  <Card key={key} size="small" style={{ marginBottom: 8 }}>
                    <Row gutter={12} align="middle">
                      <Col xs={24} md={12}>
                        <span>{label}</span>
                      </Col>
                      <Col xs={24} md={12} style={{ textAlign: "right" }}>
                        <Radio.Group
                          value={r.result}
                          onChange={(e) => updateResult(key, { result: e.target.value })}
                          optionType="button"
                          buttonStyle="solid"
                          size="small"
                        >
                          <Radio.Button value="pass">Pass</Radio.Button>
                          <Radio.Button value="fail">Fail</Radio.Button>
                          <Radio.Button value="na">N/A</Radio.Button>
                        </Radio.Group>
                      </Col>
                    </Row>
                    {r.result === "fail" && (
                      <Input
                        placeholder="Describe the issue"
                        value={r.comment}
                        onChange={(e) => updateResult(key, { comment: e.target.value })}
                        style={{ marginTop: 8 }}
                      />
                    )}
                    {/* Photos are allowed on ANY item (not only fails) so farmers can
                        attach evidence even when an item passes. */}
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 12, color: "rgba(0,0,0,0.45)", marginBottom: 4 }}>
                        Attach photos (optional)
                      </div>
                      <PhotoGrid>
                        <FileUploader
                          name="Image[file]"
                          type="inspection"
                          multiple
                          isCompressFile
                          listType="picture"
                          handleOnSuccess={(image) => handlePhotoSuccess(key, image)}
                          handleOnRemove={(file) => handlePhotoRemove(key, file)}
                        />
                      </PhotoGrid>
                    </div>
                  </Card>
                );
              })}
            </div>
          ))}

          <Divider orientation="left">Notes & Recipients</Divider>
          <TextArea
            rows={3}
            placeholder="General notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ marginBottom: 12 }}
          />
          <label>Email report to</label>
          <Select
            mode="tags"
            value={recipients}
            onChange={setRecipients}
            style={{ width: "100%" }}
            placeholder="Select people or type email addresses"
            tokenSeparators={[",", " "]}
            optionFilterProp="children"
          >
            {users
              .filter((u) => u.email)
              .map((u) => (
                <Option key={u.email} value={u.email}>
                  {u.name} {u.surname} ({u.email})
                </Option>
              ))}
          </Select>
        </>
      )}
    </Modal>
  );
};

export default PreStartInspectionModal;
